import { createContext } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Button,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { GPTSelector } from './GPTSelector';
import { ChatHistory } from './ChatHistory';
import { UserMenu } from './UserMenu';

// Context for sidebar state
export const SidebarContext = createContext();

const SIDEBAR_WIDTH = 256;
const COLLAPSED_WIDTH = 64;

export const Sidebar = ({
  isOpen,
  isCollapsed,
  onClose,
  onCollapse,
  onExpand,
  onNewChat,
  onSearchChats,
  onSelectGPT,
  onSelectThread,
  selectedGptId,
  threads,
  sharedThreads = [],
  currentThreadId,
  customGpts,
  user,
  onOpenProfile,
  onLogout,
  onOpenCompanyAdmin,
  apiUrl,
  onRenameThread,
  onDeleteThread,
  onLeaveThread,
  onPinThread
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sidebarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Desktop Header with Collapse Button */}
      {!isMobile && (
        <Box sx={{ display: 'flex', px: 1.5, pt: 1, pb: 1, justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onCollapse}
            size="small"
            sx={{ color: 'text.secondary' }}
            title="Collapse sidebar"
          >
            <MenuOpenIcon />
          </IconButton>
        </Box>
      )}

      {/* Mobile Header with Close Button */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* New Chat Button */}
      <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
        <Button
          fullWidth
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            justifyContent: 'flex-start',
            px: 1.5,
            py: 0.75,
            textTransform: 'none',
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          New Chat
        </Button>
      </Box>


      {/* Separator */}
      <Divider sx={{ mb: 1.5 }} />

      {/* GPT Selector */}
      <GPTSelector
        selectedGptId={selectedGptId}
        onSelectGPT={onSelectGPT}
        onCloseMobile={onClose}
        customGpts={customGpts}
        apiUrl={apiUrl}
      />

      {/* Chat History */}
      <ChatHistory
        threads={threads}
        sharedThreads={sharedThreads}
        currentThreadId={currentThreadId}
        onSelectThread={onSelectThread}
        onCloseMobile={onClose}
        onRenameThread={onRenameThread}
        onDeleteThread={onDeleteThread}
        onLeaveThread={onLeaveThread}
        onSearchChats={onSearchChats}
        onPinThread={onPinThread}
      />

      {/* User Menu */}
      <UserMenu
        user={user}
        onOpenProfile={onOpenProfile}
        onLogout={onLogout}
        onOpenCompanyAdmin={onOpenCompanyAdmin}
      />
    </Box>
  );

  const collapsedContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: COLLAPSED_WIDTH,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        pt: 1,
      }}
    >
      {/* Expand Button */}
      <IconButton
        onClick={onExpand}
        size="small"
        sx={{ mb: 1, color: 'text.secondary' }}
        title="Expand sidebar"
      >
        <ChevronRightIcon />
      </IconButton>

      {/* New Chat Icon */}
      <IconButton
        onClick={onNewChat}
        size="small"
        sx={{ mb: 1, color: 'text.secondary' }}
        title="New Chat"
      >
        <AddIcon />
      </IconButton>

      {/* Search Icon */}
      <IconButton
        onClick={onSearchChats}
        size="small"
        sx={{ color: 'text.secondary' }}
        title="Search Chats"
      >
        <SearchIcon />
      </IconButton>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={isOpen && isMobile}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Desktop Sidebar - Full */}
      {!isMobile && !isCollapsed && (
        <Box
          component="aside"
          id="sidebar"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Desktop Sidebar - Collapsed */}
      {!isMobile && isCollapsed && (
        <Box
          component="aside"
          id="sidebarCollapsed"
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {collapsedContent}
        </Box>
      )}
    </>
  );
};
